import { BookmarkIcon, TrashIcon } from '@radix-ui/react-icons';
import type { RowData } from '@tanstack/react-table';
import { useId, useState, type FormEvent } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { createLocalStore, useLocalStore, type LocalStore } from '@/lib/local-store';
import type { ListSearch } from '@/lib/url-state';
import type { DataTableController, TableView } from './use-data-table';

const MAX_VIEWS = 20;

export interface SavedView {
  name: string;
  search: Record<string, unknown>;
  view: TableView;
}

const savedViewsSchema = z
  .array(
    z.object({
      name: z.string().min(1).max(60),
      search: z.record(z.string(), z.unknown()),
      view: z.object({
        columnVisibility: z.record(z.string(), z.boolean()),
        density: z.enum(['comfortable', 'compact']),
      }),
    }),
  )
  .max(MAX_VIEWS);

const stores = new Map<string, LocalStore<SavedView[]>>();

/** Named filter/sort/column combinations, kept in this browser (`rgs.table.<id>.saved`). */
export function savedViewsStore(tableId: string): LocalStore<SavedView[]> {
  let store = stores.get(tableId);
  if (!store) {
    store = createLocalStore(`rgs.table.${tableId}.saved`, savedViewsSchema, []);
    stores.set(tableId, store);
  }
  return store;
}

export function DataTableSavedViews<TData extends RowData, TSearch extends ListSearch>({
  controller,
}: {
  controller: DataTableController<TData, TSearch>;
}) {
  const [views, setViews] = useLocalStore(savedViewsStore(controller.id));
  const [naming, setNaming] = useState(false);

  const apply = (saved: SavedView) => {
    controller.replaceSearch(saved.search as TSearch);
    controller.applyView(saved.view);
  };

  const save = (name: string) => {
    const { page: _page, ...search } = controller.search;
    const entry: SavedView = {
      name,
      search,
      view: { columnVisibility: controller.columnVisibility, density: controller.density },
    };
    setViews((prev) => [entry, ...prev.filter((v) => v.name !== name)].slice(0, MAX_VIEWS));
    setNaming(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <BookmarkIcon /> Views
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {views.length > 0 ? (
            <>
              <DropdownMenuLabel className="text-xs text-muted-foreground">Saved views</DropdownMenuLabel>
              {views.map((saved) => (
                <DropdownMenuItem key={saved.name} onSelect={() => apply(saved)}>
                  <span className="truncate">{saved.name}</span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          ) : (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              Save the current filters, sort and columns to come back to them.
            </p>
          )}
          <DropdownMenuItem onSelect={() => setNaming(true)}>Save current view…</DropdownMenuItem>
          {views.length > 0 && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Delete a view</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {views.map((saved) => (
                  <DropdownMenuItem
                    key={saved.name}
                    onSelect={() => setViews((prev) => prev.filter((v) => v.name !== saved.name))}
                  >
                    <TrashIcon /> <span className="truncate">{saved.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <SaveViewDialog open={naming} onOpenChange={setNaming} existing={views.map((v) => v.name)} onSave={save} />
    </>
  );
}

function SaveViewDialog({
  open,
  onOpenChange,
  existing,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing: string[];
  onSave: (name: string) => void;
}) {
  const inputId = useId();
  const [name, setName] = useState('');
  const trimmed = name.trim();
  const replaces = existing.includes(trimmed);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!trimmed) return;
    onSave(trimmed);
    setName('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Save view</DialogTitle>
            <DialogDescription>Filters, sort, columns and density, saved in this browser.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor={inputId}>Name</Label>
            <Input id={inputId} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} autoFocus />
            {replaces && <p className="text-xs text-muted-foreground">This replaces the saved view with that name.</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!trimmed}>
              Save view
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
